import React from "react";
import cx from "classnames";
import { DateTime } from "luxon";
import { useStaticQuery, graphql } from "gatsby";

import Location from "~/views/identity/location";
import Event from "~/models/event";

type Props = {
  className?: string;
};

function FeaturedEvents({ className }: Props) {
  const { collectionEventYaml } = useStaticQuery(
    graphql`
      query FeaturedEvents {
        collectionEventYaml(slug: { eq: "featured" }) {
          id
          events {
            id
            title
            organizer
            startAt
            endAt
            venue
            url
            time_tbd
            image {
              imgix {
                url(imgixParams: { w: 320, dpr: 2 })
              }
            }
          }
        }
      }
    `
  );

  const events = collectionEventYaml.events.map(
    (event: any) => new Event(event)
  );

  const filteredEvents = events.filter(
    (event: Event) => event.endAt > DateTime.now()
  );

  return (
    <div className={cx(className)}>
      {filteredEvents.length > 0 && (
        <>
          <p className="font-extrabold text-4xl text-neutral-90 mb-6">
            Featured Events
          </p>

          <ul>
            {filteredEvents.map((event: Event) => (
              <li key={event.id} className="mb-12 last:mb-0">
                <EventCard event={event} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default FeaturedEvents;

type CardProps = {
  event: Event;
  className?: string;
};

function EventCard({ event, className }: CardProps) {
  return (
    <div className={cx(className, "bg-neutral-99 rounded-xl px-2 pt-2 pb-4")}>
      <a
        href={event.url}
        target="_blank"
        rel="noreferrer"
        className="group block relative rounded-lg mb-4 overflow-hidden isolate">
        <img
          src={event.image.imgix?.url}
          alt={event.title}
          loading="eager"
          className="w-full h-full object-cover"
        />
        <div className="border border-neutral-90-0.6 absolute inset-0 rounded-lg"></div>
      </a>
      <div className="px-1">
        <div className="text-3xs font-semibold mb-1">{event.dateLabel}</div>

        <a
          aria-label={event.title}
          href={event.url}
          rel="noopener noreferrer"
          target="_blank"
          className="block text-sm font-bold leading-[1.4] mb-2 hover:underlined-link">
          {event.title}
        </a>

        {event.venue && (
          <div className="flex gap-1 items-center">
            <Location />
            <span className="text-4xs text-neutral-60 font-medium">
              {event.venue}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
